import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '@forwardflow/store/appStore'
import {
   IStage,
   useOnboardingStoreV2,
} from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup } from '@forwardflow/types/onboarding'
import { initialStageMappingsGroups } from '@forwardflow/constants/onboarding.constants'
import {
   useGetFubStages,
   useGetPeopleTags,
   useGetSuggestedStageMappings,
} from '@forwardflow/queries/onboarding.queries'
import { StageMappingService } from '@forwardflow/services/stage-mapping.service'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import { Chip, ErrorLine, SearchPick, Skeleton } from '../ui/primitives'
import { errorText } from '../../utils/format'
import { MAX_SUGGESTION_TAGS } from '../../utils/constants'
import {
   addItem,
   allStops,
   applyAllSuggestions,
   chosenFor,
   hasAnySelection,
   itemKey,
   removeItem,
   STOP_QUESTIONS,
   suggestedFor,
   TMappedItem,
   TStopGroup,
} from '../../utils/stops'

/**
 * Steps 9 to 11: which leads to leave alone, one question per screen.
 *
 * Forward Flow calls every lead in Follow Up Boss except the ones these
 * three lists point out. Suggestions from the account are pre-applied; the
 * customer confirms, removes, or searches to add. The last question also
 * writes the mapping to the organization, so Settings → Stage Mapping shows
 * the same thing.
 */
export const StopsStep = ({ group }: { group: TStopGroup }) => {
   const q = STOP_QUESTIONS.find((x) => x.id === group) || STOP_QUESTIONS[0]
   const index = STOP_QUESTIONS.indexOf(q)
   const last = index === STOP_QUESTIONS.length - 1
   const { currentOrganization } = useAppStore()
   const { onboardingData, getNextStep, setStageMappings } =
      useOnboardingStoreV2()
   const orgId = onboardingData.organizationId || currentOrganization || ''

   const { data: fubStages, isLoading: loadingStages } = useGetFubStages(
      orgId,
      10000,
      1,
      true
   )
   const { data: fubTags, isLoading: loadingTags } = useGetPeopleTags(
      orgId,
      1,
      10000,
      true
   )
   const stages: IStage[] = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({
               id: String(s.stageId),
               name: s.stageName,
               description: '',
               type: 'stage' as const,
            })) || [],
      [fubStages]
   )
   const tags: IStage[] = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .map((t) => ({
               id: t.tagName,
               name: t.tagName,
               description: '',
               type: 'tag' as const,
            })) || [],
      [fubTags]
   )
   const stagesForSuggestion = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({ stageId: s.stageId, stageName: s.stageName })) || [],
      [fubStages]
   )
   // The suggestion service reads every name it is sent; an account with
   // hundreds of tags pushed it past the gateway's timeout, so the list is
   // capped. Every tag is still searchable below.
   const tagsForSuggestion = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .slice(0, MAX_SUGGESTION_TAGS)
            .map((t, i) => ({ tagId: i + 1, tagName: t.tagName })) || [],
      [fubTags]
   )
   const {
      data: suggested,
      isLoading: loadingSuggestions,
      error: suggestionError,
   } = useGetSuggestedStageMappings(
      stagesForSuggestion,
      tagsForSuggestion,
      !loadingStages && !loadingTags
   )
   const suggestions = suggested?.data as IStageMapGroup[] | undefined

   const groups: IStageMapGroup[] = onboardingData.stageMappings?.length
      ? onboardingData.stageMappings
      : initialStageMappingsGroups
   const loading = loadingStages || loadingTags
   // Waiting only while a request can still answer: a failed one (the
   // gateway timed out) leaves the search as the way in.
   const stillWaiting =
      !loading &&
      !suggestionError &&
      (loadingSuggestions ||
         (!suggested &&
            (stagesForSuggestion.length > 0 || tagsForSuggestion.length > 0)))
   /* The suggestion call can hang; after 10s the ghost chip stops pretending. */
   const [suggestionsTimedOut, setSuggestionsTimedOut] = useState(false)
   useEffect(() => {
      if (!stillWaiting || suggestionsTimedOut) return
      const h = window.setTimeout(() => setSuggestionsTimedOut(true), 10000)
      return () => window.clearTimeout(h)
   }, [stillWaiting, suggestionsTimedOut])
   const waitingForSuggestions = stillWaiting && !suggestionsTimedOut

   // Pre-fill the suggestions once, only on a mapping nobody has touched.
   const prefilled = useRef(false)
   useEffect(() => {
      if (prefilled.current || !suggestions?.length) return
      prefilled.current = true
      if (hasAnySelection(groups)) return
      setStageMappings(applyAllSuggestions(groups, suggestions))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [suggestions])

   const chosen = chosenFor(groups, q)
   const offered = suggestedFor(suggestions, groups, q)
   // Only what the three questions already hold is out of the search; a
   // stage the suggestions put under "leads to work" can still be picked
   // here, and moves.
   const taken = useMemo(() => new Set(allStops(groups).map(itemKey)), [groups])
   const options = useMemo(
      () => [
         ...stages
            .filter((s) => !taken.has(itemKey({ id: s.id, type: 'stage' })))
            .map((s) => ({
               id: `stage:${s.id}`,
               label: s.name,
               kind: 'stage',
            })),
         ...tags
            .filter((t) => !taken.has(itemKey({ id: t.id, type: 'tag' })))
            .map((t) => ({ id: `tag:${t.id}`, label: t.name, kind: 'tag' })),
      ],
      [stages, tags, taken]
   )
   const add = (item: TMappedItem, bucket = q.target) =>
      setStageMappings(addItem(groups, item, bucket))
   const pick = (value: string) => {
      const [type, ...rest] = value.split(':')
      const id = rest.join(':')
      const source = type === 'tag' ? tags : stages
      const found = source.find((s) => String(s.id) === id)
      if (found) add({ ...found, type: type as 'stage' | 'tag' })
   }

   const [saving, setSaving] = useState(false)
   const [saveError, setSaveError] = useState<string | null>(null)
   const next = async () => {
      if (!last) {
         getNextStep()
         return
      }
      setSaving(true)
      setSaveError(null)
      try {
         setStageMappings(groups)
         await StageMappingService.updateStageMapping({ stageMapping: groups })
         getNextStep()
      } catch (e) {
         setSaveError(errorText(e, "We couldn't save your stage mapping."))
      } finally {
         setSaving(false)
      }
   }

   useStepFooter(
      loading
         ? 'Reading your stages'
         : chosen.length
           ? last
              ? 'Looks right, continue'
              : 'Next question'
           : 'Pick at least one to continue',
      () => void next(),
      {
         disabled: loading || chosen.length === 0,
         loading: saving,
         hint: `Question ${index + 1} of 3 · Everything not listed here gets called. Editable later in Settings → Stage Mapping.`,
         secondary: saveError
            ? {
                 label: 'Continue anyway, fix it later in Settings',
                 onClick: () => getNextStep(),
              }
            : null,
         testId: `confirm-stops-${group}`,
      }
   )

   return (
      <Screen
         stepKey={`stops-${group}`}
         kind="stops"
         title={q.question}
         lede={q.hint}
         width="md"
      >
         {loading ? (
            <div className="flex flex-wrap justify-center gap-2">
               <Skeleton className="h-11 w-36 rounded-full" />
               <Skeleton className="h-11 w-28 rounded-full" />
               <Skeleton className="h-11 w-32 rounded-full" />
            </div>
         ) : (
            <div
               className="flex flex-wrap items-center justify-center gap-2"
               data-testid={`stop-${group}-chosen`}
            >
               {chosen.map((item) => (
                  <Chip
                     key={itemKey(item)}
                     kind={item.type}
                     size="lg"
                     onRemove={() =>
                        setStageMappings(removeItem(groups, item, item.bucket))
                     }
                     testId={`chip-${item.type}-${item.id}`}
                  >
                     {item.name}
                  </Chip>
               ))}
               {waitingForSuggestions && (
                  <Skeleton className="h-11 w-28 rounded-full" />
               )}
               {offered.map((item) => (
                  <Chip
                     key={itemKey(item)}
                     kind={item.type}
                     size="lg"
                     onAdd={() => add(item, item.bucket)}
                     testId={`suggest-${item.type}-${item.id}`}
                  >
                     {item.name}
                  </Chip>
               ))}
               {!chosen.length && !offered.length && !waitingForSuggestions && (
                  <p className="text-[14px] text-[var(--ob-text-3)]">
                     Nothing yet. Search your stages and tags below.
                  </p>
               )}
            </div>
         )}
         <div className="mt-5 max-w-[520px] mx-auto">
            <SearchPick
               items={options}
               placeholder="Search your stages and tags to add more"
               emptyLabel={
                  loading
                     ? 'Reading your stages and tags'
                     : 'Nothing in Follow Up Boss matches'
               }
               onPick={pick}
               keepOpen
               testId={`stop-${group}-search`}
            />
         </div>
         {!loading && !tags.length && (
            <p
               className="mt-3 text-center text-[13px] text-[var(--ob-text-3)]"
               data-testid="stops-tags-pending"
            >
               Tags show up here once your leads finish importing. You can
               add tag rules any time in Settings, Stage mapping.
            </p>
         )}
         {saveError && <ErrorLine testId="stops-error">{saveError}</ErrorLine>}
      </Screen>
   )
}

export const StopsWorkingStep = () => <StopsStep group="working" />
export const StopsDoneStep = () => <StopsStep group="done" />
export const StopsNeverStep = () => <StopsStep group="never" />
